import { action, computed, flow, makeObservable, observable } from "mobx";
import { API } from "aws-amplify";
import {
  getDocumentFolder,
  putDocumentFolder,
  getDocumentFiles,
  putDocumentFileUpload,
} from "../../../../graphql/queries";
import { StatusFilterEnum } from "../enums/StatusFilterEnum";

export class VaultStore {
  data = [];
  files = [];
  totalRowCount = 0;
  totalFilesCount = 0;
  isLoadingAll = false;
  isLoadingFiles = false;
  error = {
    show: false,
    severity: "error",
    title: "",
    message: "",
  };
  customer = null;
  currentFolderId = null;

  constructor() {
    makeObservable(this, {
      data: observable,
      files: observable,
      totalRowCount: observable,
      totalFilesCount: observable,
      isLoadingAll: observable,
      isLoadingFiles: observable,
      error: observable,
      customer: observable,
      currentFolderId: observable,
      setData: action,
      setFiles: action,
      setTotalRowCount: action,
      setTotalFilesCount: action,
      showAlert: action,
      closeAlert: action,
      isLoading: computed,
      fetchDocumentFolders: flow,
      fetchDocumentFiles: flow,
      createDocumentFolder: flow,
      renameDocumentFile: flow,
      archiveDocumentFile: flow,
      archiveDocumentFolder: flow,
      renameDocumentFolder: flow,
    });
  }

  get isLoading() {
    return this.isLoadingAll || this.isLoadingFiles;
  }

  setData(data) {
    this.data = data;
  }

  setFiles(files) {
    this.files = files;
  }

  setTotalRowCount(count) {
    this.totalRowCount = count;
  }

  setTotalFilesCount(count) {
    this.totalFilesCount = count;
  }

  showAlert({ title, message, severity = "error" }) {
    this.error.show = true;
    this.error.title = title;
    this.error.message = message;
    this.error.severity = severity;
  }

  closeAlert() {
    this.error.show = false;
    this.error.title = "";
    this.error.message = "";
    this.error.severity = "error";
  }

  *fetchDocumentFolders(customerId, search = "", status = StatusFilterEnum.ALL) {
    try {
      this.isLoadingAll = true;
      this.customer = customerId;
      const response = yield API.graphql({
        query: getDocumentFolder,
        variables: {
          input: {
            customer_id: customerId,
            search,
            status: status === StatusFilterEnum.ALL ? "" : status.toLocaleLowerCase(),
          },
        },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      if (response.data.getDocumentFolder.statusCode === "200") {
        const result = JSON.parse(response.data.getDocumentFolder.body);
        this.setData(result || []);
        this.setTotalRowCount(result?.length || 0);
      } else {
        throw new Error("Error fetching document folders");
      }
    } catch (error) {
        console.log(error)
      this.showAlert({
        severity: "error",
        title: "Error",
        message: "An error occurred while fetching document folders",
      });
    } finally {
      this.isLoadingAll = false;
    }
  }

  *createDocumentFolder(name, description, userId) {
    try {
      this.isLoadingAll = true;
      const response = yield API.graphql({
        query: putDocumentFolder,
        variables: {
          input: {
            customer_id: this.customer,
            name,
            description,
            created_by: userId,
            status: StatusFilterEnum.ACTIVE,
          },
        },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      const result = JSON.parse(response.data.putDocumentFolder.body);

      if (response.data.putDocumentFolder.statusCode === "200") {
        yield this.fetchDocumentFolders(this.customer);
        return true;
      } else {
        throw new Error(result.message || "Error creating document folder");
      }
    } catch (error) {
      this.showAlert({
        severity: "error",
        title: "Error",
        message: "An error occurred while creating the document folder",
      });
      console.error("Error creating document folder:", error);
      return false;
    } finally {
      this.isLoadingAll = false;
    }
  }

  *fetchDocumentFiles(folderId, customerId, search = "", status = StatusFilterEnum.ALL, createdBy = "") {
    try {
      this.isLoadingFiles = true;
      this.currentFolderId = folderId;

      const response = yield API.graphql({
        query: getDocumentFiles,
        variables: {
          input: {
            folder_id: folderId,
            customer_id: customerId,
            search,
            status: status === StatusFilterEnum.ALL ? "" : status.toLocaleLowerCase(),
            created_by: createdBy,
          },
        },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      if (response.data.getDocumentFiles.statusCode === "200") {
        const result = JSON.parse(response.data.getDocumentFiles.body);
        this.setFiles(result.items || []);
        this.setTotalFilesCount(result.items?.length || 0);
      } else {
        throw new Error("Error fetching document files");
      }
    } catch (error) {
        console.log(error)
      this.showAlert({
        severity: "error",
        title: "Error",
        message: "An error occurred while fetching document files",
      });
      console.error("Error fetching document files:", error);
    } finally {
      this.isLoadingFiles = false;
    }
  }

  *renameDocumentFile(folderId, customerId, fileId, newName, userId, status) {
    try {
      this.isLoadingFiles = true;
      const response = yield API.graphql({
        query: putDocumentFileUpload,
        variables: {
          input: {
            customer_id: customerId,
            folder_id: folderId,
            id: fileId,
            name: newName,
            status: status,
            created_by: userId,
            file_type: "document"
          },
        },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      if (response.data.putDocumentFileUpload.statusCode === "200") {
        yield this.fetchDocumentFiles(folderId, customerId, "", StatusFilterEnum.ALL, "");
        return true;
      } else {
        throw new Error("Error renaming document file");
      }
    } catch (error) {
      this.showAlert({
        severity: "error",
        title: "Error",
        message: "An error occurred while renaming the document",
      });
      console.error("Error renaming document:", error);
      return false;
    } finally {
      this.isLoadingFiles = false;
    }
  }

  *archiveDocumentFile(folder_id, customerId, fileId, name, userId) {
    try {
      this.isLoadingFiles = true;
      const response = yield API.graphql({
        query: putDocumentFileUpload,
        variables: {
          input: {
            customer_id: customerId,
            folder_id: folder_id,
            status: StatusFilterEnum.ARCHIVED,
            created_by: userId,
            id: fileId,
            name: name,
          },
        },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      if (response.data.putDocumentFileUpload.statusCode === "200") {
        yield this.fetchDocumentFiles(this.currentFolderId, customerId, "", StatusFilterEnum.ALL, "");
        return true;
      } else {
        throw new Error("Error archiving document file");
      }
    } catch (error) {
      this.showAlert({
        severity: "error",
        title: "Error",
        message: "An error occurred while archiving the document",
      });
      console.error("Error archiving document:", error);
      return false;
    } finally {
      this.isLoadingFiles = false;
    }
  }

  *archiveDocumentFolder(props) {
    const { folderId, newName, description, created_by, files_stored } = props;
    try {
      this.isLoadingAll = true;
      const response = yield API.graphql({
        query: putDocumentFolder,
        variables: {
          input: {
            customer_id: this.customer,
            id: folderId,
            name: newName,
            description: description,
            created_by,
            status: StatusFilterEnum.ARCHIVED,
            files_stored: files_stored,
          },
        },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      if (response.data.putDocumentFolder.statusCode === "200") {
        yield this.fetchDocumentFolders(this.customer);
        return true;
      } else {
        throw new Error("Error archiving document folder");
      }
    } catch (error) {
      this.showAlert({
        severity: "error",
        title: "Error",
        message: "An error occurred while archiving the folder",
      });
      console.error("Error archiving folder:", error);
      return false;
    } finally {
      this.isLoadingAll = false;
    }
  }

  *renameDocumentFolder(props) {
    const { folderId, newName, description, created_by, files_stored } = props;
    try {
      this.isLoadingAll = true;
      const response = yield API.graphql({
        query: putDocumentFolder,
        variables: {
          input: {
            customer_id: this.customer,
            id: folderId,
            name: newName,
            description: description,
            created_by: created_by,
            status: StatusFilterEnum.ACTIVE,
            files_stored: files_stored,
          },
        },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      if (response.data.putDocumentFolder.statusCode === "200") {
        yield this.fetchDocumentFolders(this.customer);
        return true;
      } else {
        throw new Error("Error renaming document folder");
      }
    } catch (error) {
      this.showAlert({
        severity: "error",
        title: "Error",
        message: "An error occurred while renaming the folder",
      });
      return false;
    } finally {
      this.isLoadingAll = false;
    }
  }
}

export const vaultStore = new VaultStore();
